import { useState } from 'react';
import { api, saveSession, type Session } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import { Catalog, Overview, PlanBilling } from '../components/AdminBilling';
import { Usage } from '../components/AdminUsage';
import { StatePicker } from '../components/StatePicker';
import { stateRules } from '@njpos/core';

type Tab = 'overview' | 'usage' | 'clients' | 'catalog' | 'prices';
const TABS: [Tab, string][] = [['overview', 'Income and bills'], ['usage', 'Usage'], ['clients', 'Clients'], ['catalog', 'Plans and add-ons'], ['prices', 'Price-list agent']];

interface Client {
  id: string; name: string; slug: string; status: 'active' | 'suspended'; plan_code: string | null; service_name: string | null; service_url: string | null;
  provision_status: 'shared' | 'pending' | 'ready' | 'failed'; provision_error: string | null; notes: string | null;
  created_at: string; stores: number; users: number; owner_email: string | null;
}
interface Creds { ownerEmail: string; ownerPassword: string; url?: string | null }

const CONTAINER_CHIP: Record<Client['provision_status'], [string, string]> = {
  ready: ['good', 'Container live'], pending: ['warn', 'Deploying'], failed: ['bad', 'Deploy failed'], shared: ['', 'Shared app'],
};

// Platform admin portal: add clients, give each its own Cloud Run container, suspend or reset them.
export default function Admin({ session }: { session: Session }) {
  const clients = useLoad(() => api<Client[]>('/api/admin/clients'), []);
  const [adding, setAdding] = useState(false);
  const [creds, setCreds] = useState<Creds | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [billing, setBilling] = useState<string | null>(null);
  const catalog = useLoad(() => api<any>('/api/admin/catalog'), []);
  const planName = (code: string | null) => catalog.data?.plans.find((p: any) => p.code === code)?.name;

  async function act(id: string, what: string, fn: () => Promise<any>) {
    setBusy(`${id}:${what}`); setErr(null);
    try { const r = await fn(); clients.reload(); return r; } catch (e) { setErr(e); } finally { setBusy(null); }
  }
  const list = clients.data ?? [];
  const live = list.filter((c) => c.status === 'active').length;

  return (
    <div className="shell">
      <nav className="rail" aria-label="Admin">
        <div className="brand">PourPoint.AI</div>
        <div className="store">Admin portal</div>
        {TABS.map(([k, l]) => <a key={k} href="#" className={tab === k ? 'active' : ''} onClick={(e) => { e.preventDefault(); setTab(k); }}>{l}</a>)}
        <div className="spacer" />
        <div className="who">{session.user.name}</div>
        <button onClick={() => saveSession(null)}>Sign out</button>
      </nav>
      <main className="page">
        {tab === 'overview' && <div className="stack"><div className="page-head"><div className="grow"><h1>Income and bills</h1><p>What each client pays for and what you earn each month.</p></div></div><Overview onOpen={setBilling} /></div>}
        {tab === 'usage' && <div className="stack"><div className="page-head"><div className="grow"><h1>Usage</h1><p>How many clients, stores and people are using the POS. Demo stores are counted apart.</p></div></div><Usage /></div>}
        {tab === 'catalog' && <div className="stack"><div className="page-head"><div className="grow"><h1>Plans and add-ons</h1></div></div><Catalog /></div>}
        {tab === 'prices' && <div className="stack"><CplAgent /><CplVendors /></div>}
        {tab === 'clients' && <div className="stack">
          <div className="page-head">
            <div className="grow"><h1>Clients</h1><p>{list.length} clients, {live} active. Each new client gets its own Cloud Run container and a private web address.</p></div>
            <button className="primary" onClick={() => setAdding(true)}>Add client</button>
          </div>
          <ErrorNote error={clients.error ?? err} />
          <div className="panel"><div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Client</th><th>Owner login</th><th>Plan</th><th className="n">Stores</th><th className="n">Users</th><th>Container</th><th>Status</th><th /></tr></thead>
              <tbody>{list.map((c) => {
                const [cls, label] = CONTAINER_CHIP[c.provision_status];
                return (
                  <tr key={c.id}>
                    <td><div style={{ fontWeight: 600 }}>{c.name}</div>
                      {c.service_url ? <a href={c.service_url} target="_blank" rel="noreferrer" className="faint">{c.service_url.replace('https://', '')}</a> : <div className="faint">{c.slug}</div>}</td>
                    <td>{c.owner_email ?? '—'}</td>
                    <td>{c.plan_code ? planName(c.plan_code) ?? c.plan_code : <span className="faint">not set</span>}</td>
                    <td className="n">{c.stores}</td>
                    <td className="n">{c.users}</td>
                    <td><span className={`chip ${cls}`}>{label}</span>{c.provision_error && <div className="faint" style={{ maxWidth: 260 }}>{c.provision_error}</div>}</td>
                    <td><span className={`chip ${c.status === 'active' ? 'good' : 'bad'}`}>{c.status}</span></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="primary" onClick={() => setBilling(c.id)}>Plan and billing</button>{' '}
                      <button disabled={!!busy} onClick={() => act(c.id, 'deploy', () => api(`/api/admin/clients/${c.id}/provision`, { body: {} }))}>
                        {busy === `${c.id}:deploy` ? 'Deploying…' : c.provision_status === 'ready' ? 'Redeploy' : 'Create container'}</button>{' '}
                      <button disabled={!!busy} onClick={async () => {
                        const r = await act(c.id, 'pw', () => api<Creds>(`/api/admin/clients/${c.id}/reset-password`, { body: {} }));
                        if (r) setCreds({ ...r, url: c.service_url });
                      }}>Reset password</button>{' '}
                      <button disabled={!!busy} onClick={() => {
                        if (c.status === 'active' && !confirm(`Suspend ${c.name}? Nobody there will be able to sign in or ring sales.`)) return;
                        act(c.id, 'status', () => api(`/api/admin/clients/${c.id}/status`, { body: { status: c.status === 'active' ? 'suspended' : 'active' } }));
                      }}>{c.status === 'active' ? 'Suspend' : 'Resume'}</button>
                    </td>
                  </tr>);
              })}</tbody>
            </table>
            {clients.data && list.length === 0 && <div className="empty">No clients yet. Add your first one.</div>}
          </div></div>
        </div>}
      </main>
      {billing && <PlanBilling clientId={billing} onClose={() => setBilling(null)} onDone={() => clients.reload()} />}
      {adding && <AddClient plans={catalog.data?.plans ?? []} addons={catalog.data?.addons ?? []} onClose={() => setAdding(false)} onDone={(c) => { setAdding(false); setCreds(c); clients.reload(); }} />}
      {creds && (
        <Modal title="Login details" onClose={() => setCreds(null)} footer={<button className="primary" onClick={() => setCreds(null)}>Done</button>}>
          <div className="notice">Copy these now. The password is not shown again.</div>
          {creds.url && <p>Address: <a href={creds.url} target="_blank" rel="noreferrer">{creds.url}</a></p>}
          <p>Email: <b>{creds.ownerEmail}</b></p>
          <p>Temporary password: <b className="num">{creds.ownerPassword}</b></p>
        </Modal>
      )}
    </div>
  );
}

function AddClient({ plans, addons, onClose, onDone }: { plans: any[]; addons: any[]; onClose: () => void; onDone: (c: Creds) => void }) {
  const [f, setF] = useState({ businessName: '', storeName: '', stateCode: 'NJ', njLicenseNo: '', address: '', ownerName: '', ownerEmail: '', notes: '' });
  const [planCode, setPlanCode] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [container, setContainer] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const ok = f.businessName.length > 1 && f.storeName.length > 1 && f.ownerName.length > 1 && /\S+@\S+\.\S+/.test(f.ownerEmail);
  async function save() {
    setBusy(true); setErr(null);
    try {
      const r = await api<any>('/api/admin/clients', { body: {
        businessName: f.businessName, storeName: f.storeName, stateCode: f.stateCode, ownerName: f.ownerName, ownerEmail: f.ownerEmail, createContainer: container, ...(planCode ? { planCode, addonCodes: picked } : {}),
        ...(f.njLicenseNo ? { njLicenseNo: f.njLicenseNo } : {}), ...(f.address ? { address: f.address } : {}), ...(f.notes ? { notes: f.notes } : {}),
      } });
      onDone({ ownerEmail: r.ownerEmail, ownerPassword: r.ownerPassword, url: r.client.service_url });
    } catch (e) { setErr(e); setBusy(false); }
  }
  return (
    <Modal title="Add client" onClose={busy ? () => {} : onClose} footer={<button className="primary" disabled={!ok || busy} onClick={save}>{busy ? (container ? 'Creating container… (up to a minute)' : 'Creating…') : 'Create client'}</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Business name<input autoFocus value={f.businessName} onChange={set('businessName')} /></label>
        <label>Store name<input value={f.storeName} onChange={set('storeName')} /></label>
      </div>
      <div className="field-row">
        <StatePicker value={f.stateCode} onChange={(stateCode) => setF({ ...f, stateCode })} />
        <label>{stateRules(f.stateCode).licenseLabel} no.<input value={f.njLicenseNo} onChange={set('njLicenseNo')} /></label>
      </div>
      <label>Address<input value={f.address} onChange={set('address')} /></label>
      <div className="field-row">
        <label>Owner name<input value={f.ownerName} onChange={set('ownerName')} /></label>
        <label>Owner email<input type="email" value={f.ownerEmail} onChange={set('ownerEmail')} /></label>
      </div>
      <label>Notes (private)<input value={f.notes} onChange={set('notes')} /></label>
      <label>Plan<select value={planCode} onChange={(e) => setPlanCode(e.target.value)}><option value="">Choose later (everything on)</option>{plans.filter((p) => p.active).map((p) => <option key={p.code} value={p.code}>{p.name} · ${(p.monthly_cents / 100).toFixed(2)}/month</option>)}</select></label>
      {planCode && addons.filter((a) => a.active).length > 0 && (
        <div><div className="faint">Add-ons</div>
          {addons.filter((a) => a.active).map((a) => (
            <label key={a.code} className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)', gap: 6 }}>
              <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={picked.includes(a.code)} onChange={(e) => setPicked(e.target.checked ? [...picked, a.code] : picked.filter((x) => x !== a.code))} /> {a.name} · ${(a.monthly_cents / 100).toFixed(2)}/month
            </label>))}
        </div>)}
      <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
        <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={container} onChange={(e) => setContainer(e.target.checked)} /> Create a dedicated Cloud Run container for this client
      </label>
      <p className="faint">A temporary owner password is generated and shown once after you create the client.</p>
    </Modal>
  );
}

const STATUS_CHIP: Record<string, string> = { ok: 'good', partial: 'warn', failed: 'bad', running: 'warn' };

// The price-list agent: runs every 5 days, combines the workbooks in the inbox and imports them. Shows each run and lets you start one.
function CplAgent() {
  const runs = useLoad(() => api<any[]>('/api/admin/cpl/agent-runs'), []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <div className="panel"><div className="panel-head"><h2 className="grow">Price-list agent</h2>
      <button disabled={busy} onClick={async () => {
        setBusy(true); setErr(null); setMsg('');
        try { await api('/api/admin/cpl/agent-run', { body: {} }); setMsg('Started. It takes a few minutes; refresh to see the result.'); setTimeout(() => runs.reload(), 4000); } catch (e) { setErr(e); } finally { setBusy(false); }
      }}>Run now</button> <button onClick={() => runs.reload()}>Refresh</button></div>
      <div className="panel-body stack">
        <p className="muted">On the 1st and 15th the agent signs in to ABC Online, downloads each vendor's price list below, combines them (plus any workbooks you drop in <code>cpl-inbox/</code> of the files bucket) into one workbook and loads it into the POS. Anything it cannot read or find is listed under the run.</p>
        <ErrorNote error={runs.error ?? err} />
        {msg && <div className="notice good">{msg}</div>}
        <table className="grid">
          <thead><tr><th>When</th><th>Trigger</th><th className="n">Files</th><th className="n">Combined</th><th className="n">Updated</th><th className="n">Unchanged</th><th className="n">Failed</th><th>Result</th><th /></tr></thead>
          <tbody>{runs.data?.map((r) => (
            <tr key={r.id}>
              <td>{new Date(r.started_at).toLocaleString()}</td><td>{r.trigger}</td><td className="n">{r.files_found}</td><td className="n">{r.files_combined}</td>
              <td className="n">{r.imported}</td><td className="n">{r.unchanged}</td><td className="n">{r.failed}</td>
              <td><span className={`chip ${STATUS_CHIP[r.status] ?? ''}`}>{r.status}</span>{r.message && <div className="faint" style={{ maxWidth: 360 }}>{r.message}</div>}
                {r.needs_review?.length > 0 && <details><summary className="faint" style={{ cursor: 'pointer' }}>{r.needs_review.length} need review</summary>{r.needs_review.map((n: any, i: number) => <div key={i} className="faint">{n.file.split('_')[2] ?? n.file}: {n.notes}</div>)}</details>}</td>
              <td>{r.files_combined > 0 && <button onClick={async () => {
                try {
                  const res = await api<Response>(`/api/admin/cpl/agent-runs/${r.id}/combined`, { raw: true });
                  const a = document.createElement('a'); a.href = URL.createObjectURL(await res.blob()); a.download = `NJ_CPL_Combined_${r.started_at.slice(0, 10)}.xlsx`; a.click();
                } catch (e) { setErr(e); }
              }}>Workbook</button>}</td>
            </tr>))}</tbody>
        </table>
        {runs.data?.length === 0 && <div className="empty">No runs yet.</div>}
      </div>
    </div>
  );
}

// The wholesalers the agent pulls. Untick one to stop pulling it; add a new one by license number.
function CplVendors() {
  const vendors = useLoad(() => api<any[]>('/api/admin/cpl/vendors'), []);
  const [f, setF] = useState({ licenseNo: '', tradeName: '' });
  const [err, setErr] = useState<unknown>(null);
  const on = vendors.data?.filter((v) => v.buy).length ?? 0;
  return (
    <div className="panel"><div className="panel-head"><h2 className="grow">Price-list vendors</h2><span className="faint">{on} of {vendors.data?.length ?? 0} pulled each time</span></div>
      <div className="panel-body stack">
        <ErrorNote error={vendors.error ?? err} />
        <div className="field-row">
          <label>License number<input value={f.licenseNo} onChange={(e) => setF({ ...f, licenseNo: e.target.value })} placeholder="3402-23-159-003" /></label>
          <label>Name as filed (what the portal search uses)<input value={f.tradeName} onChange={(e) => setF({ ...f, tradeName: e.target.value })} placeholder="ALLIED BEVERAGE GROUP LLC" /></label>
          <button disabled={!f.licenseNo || f.tradeName.length < 2} onClick={async () => {
            setErr(null);
            try { await api('/api/admin/cpl/vendors', { body: f }); setF({ licenseNo: '', tradeName: '' }); vendors.reload(); } catch (e) { setErr(e); }
          }}>Add vendor</button>
        </div>
        <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
          <table className="grid">
            <thead><tr><th>Pull</th><th>Vendor</th><th>License</th><th>Last pull</th></tr></thead>
            <tbody>{vendors.data?.map((v) => (
              <tr key={v.license_no}>
                <td><input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={v.buy} aria-label={`Pull ${v.trade_name}`}
                  onChange={async (e) => { try { await api(`/api/admin/cpl/vendors/${v.license_no}`, { method: 'PATCH', body: { buy: e.target.checked } }); vendors.reload(); } catch (x) { setErr(x); } }} /></td>
                <td>{v.trade_name}</td><td className="num">{v.license_no}</td>
                <td>{v.last_pulled_at ? <><span className={`chip ${String(v.last_result).startsWith('downloaded') ? 'good' : 'warn'}`}>{v.last_result}</span> <span className="faint">{new Date(v.last_pulled_at).toLocaleDateString()}</span></> : <span className="faint">never</span>}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
